import clsx from 'clsx';
import ReportIcon from '../../assets/icons/report-icon.svg?react';
import React from 'react';
import ChevronLeftIcon from '../../assets/icons/chevron-down-icon.svg?react';
import TextLeftIcon from '../../assets/icons/text-left-icon.svg?react';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import { Link } from 'react-router';
import Button from '../../components/Button';
import DatePicker from '../../components/DatePicker';
import TimePicker from '../../components/TimePicker';
import Input from '../../components/Input';
import Select from '../../components/Select';
import TextArea from '../../components/TextArea';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import {
  CorrectiveActionsPriorityEnum,
  GetIncidentByIdResponse,
} from '@EHSNavigator/backend';
import moment from 'moment';
import MultiAssigneeDropDown from '../../components/MultiAssigneeDropDown';
import SaveAsDraft from '../../components/SaveAsDraft';

interface IncidentReviewDetailStepOneProps {
  otherClasses?: string;
  formHandler: any;
  handleSteps: React.Dispatch<number>;
  data: GetIncidentByIdResponse['incident'];
  departments: { label: string; value: string }[];
  userList: { label: string; value: string }[];
  handleDraft: () => void;
  isDraftLoading?: boolean;
  disbaleButtons?: boolean;
  allDraftIncidentReviewsLength: number | undefined;
}

const IncidentReviewDetailStepOne: React.FC<
  IncidentReviewDetailStepOneProps
> = ({
  otherClasses,
  formHandler,
  handleSteps,
  data,
  departments,
  userList,
  handleDraft,
  isDraftLoading,
  disbaleButtons,
  allDraftIncidentReviewsLength,
}) => {
  const incidentReviewDetailStepOneClasses = clsx(otherClasses);

  const filteredUserList = userList.filter((user) =>
    formHandler.values.assignee.includes(user.value),
  );
  const lg = useButtonSizeLarge();

  return (
    <form
      action="#"
      onSubmit={formHandler?.handleSubmit}
      className={incidentReviewDetailStepOneClasses}
      data-testid="incident-review-detail-step-one"
    >
      <div className="flex flex-col items-start gap-2.5">
        <div className="flex w-full flex-wrap items-center justify-between gap-5">
          <Button
            type="button"
            onClick={() => handleSteps(1)}
            variant="link"
            otherClasses="gap-2.5 w-fit "
          >
            <span className="bg-brand-byzantine rounded-5 flex h-8 w-8 justify-center">
              <ChevronLeftIcon className="h-7 w-7 rotate-90 [&>path]:fill-white" />
            </span>
            <strong>Return</strong>
          </Button>
          <div className="flex items-center gap-3 sm:gap-5">
            <SaveAsDraft
              otherClasses="!mt-0 !py-0 border-y-transparent"
              handleSaveAsDraft={handleDraft}
              isDisabled={isDraftLoading}
              disbaleButtons={disbaleButtons}
            />
            <Button
              variant="secondary"
              size={lg}
              href="/incident-review/drafts"
              otherClasses="relative"
            >
              <EditIcon className="h-6 w-6 [&>path]:fill-current" /> All Drafts{' '}
              {allDraftIncidentReviewsLength && (
                <span className="text-brand-posh-pink">
                  {`(${allDraftIncidentReviewsLength})`}
                </span>
              )}
            </Button>
          </div>
        </div>

        <h1 className="h3 mb-1 lg:mb-0">Create Corrective Action</h1>
        <p className="text-brand-byzantine font-inter-variable -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
          STEP 1 / 2
        </p>
      </div>
      <div className="border-y-neutral-tones-text/10 my-6 border-y py-5 lg:my-9">
        <div className="bg-card-backgrounds/5 border-neutral-tones-text/10 rounded-10 flex w-full items-center gap-2.5 border p-15">
          <ReportIcon className="[&>path]:fill-brand-fair-orange h-12 w-12 md:h-9 md:w-9 lg:h-12 lg:w-12" />
          <div className="flex flex-1 flex-col gap-1 lg:gap-2.5">
            <span className="font-inter-variable text-brand-fair-orange -tracking-form-input lg:-tracking-button text-base leading-5 font-medium lg:text-xl lg:leading-6 lg:font-bold">
              Linked Incident
            </span>
            <div className="flex items-center gap-15">
              <p className="text-neutral-tones-text -tracking-form-input lg:-tracking-text-area line-clamp-1 text-base leading-5 font-bold capitalize lg:text-lg lg:leading-5">
                {data?.title}
              </p>
              <span className="border-l-neutral-tones-text/10 hidden h-5 border-l lg:block"></span>
              <p className="font-inter-variable text-neutral-tones-text/80 -tracking-form-label lg:-tracking-form-input hidden text-sm leading-5 font-normal whitespace-nowrap lg:block lg:text-base">
                Reference ID{' '}
                <span className="text-neutral-tones-text/50 uppercase">
                  {data?.incidentRefId}
                </span>
              </p>
            </div>
          </div>
          <Link target="_blank" to={`/my-reported-incidents/${data?.id}`}>
            <ChevronLeftIcon className="[&>path]:fill-neutral-tones-text h-6 w-6 -rotate-90 md:h-12 md:w-12" />
          </Link>
        </div>
      </div>
      <div className="flex w-full flex-wrap items-start gap-x-12 gap-y-9">
        <div className="flex flex-col gap-6">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Due Date & Time
          </h6>
          <div className="flex flex-col items-start gap-9 md:flex-row">
            <DatePicker
              otherClasses={{ container: '!w-235 lg:!w-235 lg:max-w-235 ' }}
              label="Date*"
              name="date"
              value={formHandler?.values?.date}
              placeholder="Select Date"
              disabled={
                formHandler.isSubmitting ||
                formHandler?.values?.dateTimeUndetermined
              }
              startMonth={moment().toDate()}
              onBlur={() => {
                if (formHandler?.values?.date) {
                  formHandler?.setFieldTouched('date', true);
                }
              }}
              endMonth={moment().add(6, 'months').toDate()}
              onChange={(e) => formHandler?.setFieldValue('date', e, true)}
              errorMessage={
                formHandler?.touched.date && formHandler?.errors.date
                  ? formHandler?.errors.date
                  : ''
              }
            />
            <TimePicker
              onBlur={formHandler?.handleBlur}
              label="Time*"
              errorMessage={
                formHandler?.touched.time && formHandler?.errors.time
                  ? formHandler?.errors.time
                  : ''
              }
              disabled={
                formHandler?.isSubmitting ||
                formHandler?.values?.dateTimeUndetermined
              }
              value={formHandler?.values.time}
              placeholder="Select Time"
              onChange={formHandler?.handleChange}
              name="time"
              otherClasses={{ container: '!w-235 lg:!w-235 lg:max-w-235 ' }}
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-6 lg:w-auto">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Assignee
          </h6>
          <div className="flex flex-col items-start gap-x-9 gap-y-6 md:flex-row">
            <Select
              label="Department*"
              list={departments}
              value={formHandler.values.department}
              disabled={formHandler.isSubmitting}
              placeholder="Departments"
              otherClasses={{ container: 'w-full lg:max-w-261 lg:w-261' }}
              onSelect={(e) =>
                formHandler?.setFieldValue('department', e, true)
              }
              errorMessage={
                formHandler?.touched.department &&
                formHandler?.errors.department
                  ? formHandler?.errors.department
                  : ''
              }
            />

            <MultiAssigneeDropDown
              label="Assignee*"
              withSearch
              disabled={formHandler.isSubmitting}
              list={userList}
              placeholder="Select Assignee"
              value={formHandler.values.assignee}
              onSelect={(e) => {
                const { assignee = [] } = formHandler?.values || {};
                const updatedAssignees = assignee?.includes(e)
                  ? assignee?.filter((item: any) => item !== e)
                  : [...assignee, e];

                formHandler?.setFieldValue('assignee', updatedAssignees, true);
                if (e === formHandler?.values?.leadAssignee) {
                  formHandler?.setFieldValue('leadAssignee', '', true);
                }
              }}
              handleRemove={(e) => {
                const { assignee = [] } = formHandler?.values || {};
                const updatedAssignees = assignee?.filter(
                  (item: any) => item !== e,
                );
                formHandler?.setFieldValue('assignee', updatedAssignees, true);
                if (e === formHandler?.values?.leadAssignee) {
                  formHandler?.setFieldValue('leadAssignee', '', true);
                }
              }}
              errorMessage={
                formHandler?.touched.assignee && formHandler?.errors.assignee
                  ? formHandler?.errors.assignee
                  : ''
              }
              otherClasses={{ container: 'w-full lg:max-w-261 lg:w-261' }}
            />
          </div>
          {formHandler.values.assignee?.length >= 2 && (
            <MultiAssigneeDropDown
              label={
                <p
                  className={clsx(
                    'text-form-label -tracking-form-label font-inter-variable flex items-center gap-2 font-bold',
                    formHandler.isSubmitting
                      ? `text-neutral-tones-text/35`
                      : `text-neutral-tones-text`,
                  )}
                >
                  Lead Assignee*{' '}
                  <button
                    type="button"
                    className="group relative cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <WarningIcon
                      className={clsx(
                        '[&>g>path]:fill-brand-byzantine h-4 w-4',
                      )}
                    />
                    <span className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-6 left-2/4 z-10 hidden min-w-211 -translate-x-2/4 items-center justify-center rounded px-2 py-1 text-center text-xs font-normal shadow-lg group-hover:block group-focus:block">
                      Primary assignee responsible for overseeing corrective
                      actions. Can request due date extension and send
                      corrective action for review
                    </span>
                  </button>
                </p>
              }
              disabled={formHandler.isSubmitting}
              list={filteredUserList}
              placeholder="Select Lead Assignee"
              value={formHandler.values.leadAssignee}
              onSelect={(e) => {
                formHandler?.setFieldValue('leadAssignee', e, true);
              }}
              handleRemove={() => {
                formHandler?.setFieldValue('leadAssignee', '', true);
              }}
              singleSelect={true}
              errorMessage={
                formHandler?.touched.leadAssignee &&
                formHandler?.errors.leadAssignee
                  ? formHandler?.errors.leadAssignee
                  : ''
              }
              otherClasses={{
                container: 'w-full ',
                label: clsx(''),
              }}
            />
          )}
        </div>
      </div>
      <div className="mt-9 flex w-full flex-col items-start gap-x-9 gap-y-9 md:flex-row lg:flex-wrap">
        <div className="flex w-full flex-col gap-6 md:w-2/4 lg:w-auto">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Action Priority*
          </h6>
          <Select
            list={Object.values(CorrectiveActionsPriorityEnum).map(
              (status) => ({
                label: status,
                value: status,
              }),
            )}
            value={formHandler.values.actionPriority}
            disabled={formHandler.isSubmitting}
            placeholder="Priorities"
            otherClasses={{ container: 'w-full lg:max-w-465 lg:w-465' }}
            onSelect={(e) =>
              formHandler?.setFieldValue('actionPriority', e, true)
            }
            errorMessage={
              formHandler?.touched.actionPriority &&
              formHandler?.errors.actionPriority
                ? formHandler?.errors.actionPriority
                : ''
            }
          />
        </div>
        <div className="flex w-full flex-col gap-6 md:w-2/4 lg:w-auto">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Action Title*
          </h6>
          <div className="flex flex-col items-start gap-x-9 gap-y-6 md:flex-row">
            <Input
              onBlur={formHandler?.handleBlur}
              errorMessage={
                formHandler?.touched.actionTitle &&
                formHandler?.errors.actionTitle
                  ? formHandler?.errors.actionTitle
                  : ''
              }
              disabled={
                formHandler?.isSubmitting ||
                formHandler?.values?.dateTimeUndetermined
              }
              value={formHandler?.values.actionTitle}
              type="text"
              placeholder="Enter action title"
              onChange={formHandler?.handleChange}
              name="actionTitle"
              icon={
                <TextLeftIcon
                  className={clsx('[&>path]:stroke-neutral-tones-text/50')}
                />
              }
              otherClasses={{
                container: 'w-full lg:max-w-465 lg:w-465',
                input: 'pl-14',
                icon: 'left-5 right-auto',
              }}
            />
          </div>
        </div>
      </div>
      <div className="mt-9 mb-6 flex w-full flex-col items-start gap-x-9 gap-y-9 md:flex-row lg:my-9 lg:flex-wrap">
        <div className="flex w-full flex-col gap-6 lg:w-auto">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Description of Action
          </h6>
          <div className="flex flex-col items-start gap-x-9 gap-y-6 md:flex-row">
            <TextArea
              name="description"
              onBlur={formHandler?.handleBlur}
              onChange={formHandler?.handleChange}
              value={formHandler?.values?.description}
              placeholder="Write something here e.g. The chemical spill must be cleaned up..."
              disabled={formHandler?.isSubmitting}
              errorMessage={
                formHandler?.touched.description &&
                formHandler?.errors.description
                  ? formHandler?.errors.description
                  : ''
              }
              label="Description*"
              otherClasses={{ container: 'w-full lg:max-w-465 lg:w-465' }}
            />
          </div>
        </div>
      </div>
      <Button
        onClick={() => handleSteps(3)}
        otherClasses="w-full max-w-185"
        size={useButtonSizeLarge()}
      >
        Next
        <ChevronIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
      </Button>
    </form>
  );
};

export default IncidentReviewDetailStepOne;
